export const HERO_COPY_VARIANTS = [
  {
    eyebrow: 'Vega · cloud-native operating system',
    title_pre: 'A better way to',
    title_verb: 'deliver software',
    title_post: ', beyond the clouds.',
    sub: 'Vega is the platform engineering layer that sits above AWS, Azure, GCP, Civo and your own metal — so your team ships, audits, and supports software through a single portal.',
  },
  {
    eyebrow: 'Platform engineering, on tap',
    title_pre: 'Adopt *aaS',
    title_verb: 'faster, cheaper',
    title_post: ', and on your own terms.',
    sub: 'Curate the software your business actually uses. Pay only for what you run. Skip the parasitic costs of training, certifications, and bespoke ops.',
  },
  {
    eyebrow: 'For Architects, Developers, Engineers',
    title_pre: 'Crafted by experts,',
    title_verb: 'for experts',
    title_post: '.',
    sub: 'Decades of cross-industry experience, distilled into a unified portal for cloud and on-prem alike. Day-1 to Day-N — without the operational drag.',
  },
];

export const PRODUCT_TRIO = [
  {
    id: 'fleet',
    label: 'Fleet Manager',
    tagline: 'One portal. Every cluster.',
    bullets: [
      'Real-time fleet health',
      'Automated reconciliation',
      'Audit-ready by default',
      'Hybrid + multi-cloud',
    ],
    grad: 'vd-grad-card-1',
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    tagline: 'Find it. One-click install. Done.',
    bullets: [
      'Curated, security-scanned',
      'Dependencies bundled',
      'Subscription billing',
      'Direct vendor support',
    ],
    grad: 'vd-grad-card-2',
  },
  {
    id: 'developer',
    label: 'Developer Portal',
    tagline: 'Build once. Ship to anyone.',
    bullets: [
      'VDK / Atlas packaging',
      'Day-1 → Day-N tooling',
      'Helpdesk built in',
      'Monetize without ops',
    ],
    grad: 'vd-grad-card-3',
  },
];

export const ATLAS_BULLETS = [
  'Self-contained application package',
  'Configuration templates baked in',
  'Runtime dependencies resolved',
  'Deployment scripts versioned',
  'Health & monitoring wired by default',
];

export const DEPLOYMENT_TARGETS = [
  { value: 'aws',    label: 'AWS' },
  { value: 'azure',  label: 'Microsoft Azure' },
  { value: 'gcp',    label: 'Google Cloud' },
  { value: 'civo',   label: 'Civo' },
  { value: 'edge',   label: 'Edge / On-prem' },
  { value: 'hybrid', label: 'Hybrid (multi-target)' },
];

export const DEPLOYMENT_PRODUCTS = [
  'Postgres Cluster',
  'Redis (cache)',
  'Kafka',
  'Observability stack',
  'Identity provider',
  'Object storage',
];

export const DEPLOYMENT_TABS = [
  { id: 'marketplace', label: 'From Marketplace' },
  { id: 'atlas',       label: 'From Atlas' },
  { id: 'vdk',         label: 'VDK build' },
];

export const SECTORS = [
  {
    id: 'dev',
    pill: 'DEV SECTOR',
    title: 'Development',
    desc: 'Free to break, fast to rebuild. Ephemeral clusters that mirror production topology without the cost.',
    chips: ['env:DEV', 'tier:free', 'auto-prune:24h'],
    grad: 'vd-grad-card-1',
  },
  {
    id: 'prod',
    pill: 'PROD SECTOR',
    title: 'Production',
    desc: 'Hardened defaults, immutable deploys, audit logs streamed to your own bucket. Scaled by the operator, not by you.',
    chips: ['env:PROD', 'audit:on', 'policy:strict'],
    grad: 'vd-grad-card-2',
  },
  {
    id: 'gpu',
    pill: 'PROD · GPU',
    title: 'Specialty / GPU',
    desc: 'Carve out workloads that need different hardware, scheduling, or compliance — without a parallel control plane.',
    chips: ['hw:GPU', 'iso:strong', 'share:opt-in'],
    grad: 'vd-grad-card-3',
  },
];

export const FAQS = [
  {
    q: 'What is Vega and what problems does it solve?',
    a: 'Vega is a platform for software delivery and management — a single portal for everything your business runs, wherever it\'s hosted. Secured, audited, easy to use.',
  },
  {
    q: 'Who should use the Vega platform?',
    a: 'Software teams who want to evaluate, deploy, and operate third-party software without standing up bespoke ops for each tool. And vendors who want to ship without owning hosting.',
  },
  {
    q: 'How do I get started?',
    a: 'Schedule a call with our team and get early access. We\'ll walk through your environment and the fastest path into production.',
  },
  {
    q: 'How does Vega handle security and compliance?',
    a: 'Continuous monitoring and automated checks across the marketplace. Submissions are scanned, audited, reported, and delivered immutably to your clusters.',
  },
  {
    q: 'Can I use Vega with my existing on-prem infrastructure?',
    a: 'Yes. Vega supports cloud and on-prem and is designed for hybrid and multi-cloud deployments out of the box.',
  },
  {
    q: 'What are the billing and licensing models?',
    a: 'Vega itself is free to use. Marketplace products bill on subscription based on users and clusters; vendor licensing terms apply per product.',
  },
  {
    q: 'Is my data safe and private on Vega?',
    a: 'Your data never leaves your accounts. The platform doesn\'t have access to it — by design.',
  },
  {
    q: 'Can I integrate Vega with my CI/CD pipelines?',
    a: 'Today the path is the Vega SDK / VDK. Native CI/CD integrations are on the roadmap.',
  },
];

// ── Deployment section — software catalog ────────────────────────────────
export const SOFTWARE_CATALOG = [
  {
    id: 'database',
    label: 'Database',
    primary: { name: 'postgres-cluster', v: 'v16.2', tag: 'Atlas · Database' },
    deps: [
      { name: 'patroni',         v: 'v3.2',  role: 'ha' },
      { name: 'pgbouncer',       v: 'v1.22', role: 'pool' },
      { name: 'backup-operator', v: 'v2.0',  role: 'backup' },
      { name: 'cert-manager',    v: 'v1.14', role: 'tls',     shared: true },
      { name: 'storage-csi',     v: 'v4.5',  role: 'storage', shared: true },
    ],
    note: 'Postgres Cluster pulls 5 dependencies; 2 already resolved across the fleet.',
  },
  {
    id: 'ai',
    label: 'AI Inference',
    primary: { name: 'triton-server', v: 'v0.9', tag: 'Atlas · AI' },
    deps: [
      { name: 'cuda-toolkit',   v: 'v12.4', role: 'runtime' },
      { name: 'model-registry', v: 'v2.1',  role: 'models' },
      { name: 'gpu-operator',   v: 'v23.9', role: 'scheduling' },
      { name: 'cert-manager',   v: 'v1.14', role: 'tls',       shared: true },
      { name: 'observability',  v: 'v2.4',  role: 'telemetry', shared: true },
    ],
    note: 'GPU stack; cert-manager and observability are reused from Database & Monitoring.',
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    primary: { name: 'observability-stack', v: 'v2.4', tag: 'Atlas · Observability' },
    deps: [
      { name: 'prometheus',     v: 'v2.52', role: 'metrics' },
      { name: 'loki',           v: 'v3.0',  role: 'logs' },
      { name: 'grafana',        v: 'v10.4', role: 'dashboards' },
      { name: 'otel-collector', v: 'v0.99', role: 'pipeline' },
      { name: 'cert-manager',   v: 'v1.14', role: 'tls', shared: true },
    ],
    note: 'Five deps with one shared; provides telemetry consumed by AI Inference.',
  },
];

export const ROLE_TONE = {
  ha:         { bg: '#e0e7ff', fg: '#3730a3' },
  pool:       { bg: '#dcfce7', fg: '#15803d' },
  backup:     { bg: '#fce7f3', fg: '#9d174d' },
  tls:        { bg: '#fef3c7', fg: '#854d0e' },
  storage:    { bg: '#e0f2fe', fg: '#075985' },
  runtime:    { bg: '#fde68a', fg: '#854d0e' },
  models:     { bg: '#ede9fe', fg: '#5b21b6' },
  scheduling: { bg: '#fce7f3', fg: '#9d174d' },
  telemetry:  { bg: '#e0e7ff', fg: '#3730a3' },
  metrics:    { bg: '#dcfce7', fg: '#15803d' },
  logs:       { bg: '#fef3c7', fg: '#854d0e' },
  dashboards: { bg: '#e0f2fe', fg: '#075985' },
  pipeline:   { bg: '#ede9fe', fg: '#5b21b6' },
};

export const SECTOR_MAPS = {
  dev: {
    pill: 'DEV SECTOR',
    accent: '#6366f1',
    soft: '#e0e7ff',
    clusters: [
      { name: 'cluster1', nodes: 2 },
      { name: 'cluster2', nodes: 3 },
      { name: 'cluster3', nodes: 3 },
      { name: 'cluster4', nodes: 5 },
    ],
  },
  prod: {
    pill: 'PROD SECTOR',
    accent: '#a855f7',
    soft: '#ede9fe',
    clusters: [
      { name: 'cluster-east', nodes: 6 },
      { name: 'cluster-west', nodes: 5 },
      { name: 'cluster-eu',   nodes: 4 },
    ],
  },
  gpu: {
    pill: 'PROD · GPU',
    accent: '#ec4899',
    soft: '#fce7f3',
    clusters: [
      { name: 'gpu-pool-1', nodes: 4 },
      { name: 'gpu-pool-2', nodes: 4 },
    ],
  },
};
